<script setup lang="ts">
import { getColorValue } from '@global-types/colors';
import { useAppDataStore } from '@stores/useAppDataStore';
import { GoogleMap, Marker, Circle } from 'vue3-google-map';
import { trans } from '@tools/translation';
import { computed, ref, watch } from 'vue';
import type {
  GoogleMapLatLngLiteral,
  GoogleMap as GoogleMapType,
  GoogleMapTransitLayer,
} from '@global-types/googleMap.ts';
import Typography from '@components/Typography.vue';

export type CircleOptions = Record<string, unknown>;

interface Props {
  latitude?: number | null,
  longitude?: number | null,
  zoom?: number,
  height?: string | number,
  isStatic?: boolean,
  hasFullscreenControl?: boolean,
  hasStreetViewControl?: boolean,
  hasZoomControl?: boolean,
  hasClickableIcons?: boolean,
  withCircle?: boolean,
  withMarker?: boolean,
  circleOptions?: CircleOptions | null,
  hasTransitLayerControl?: boolean,
  showMapTypeControl?: boolean,
  showTransit?: boolean,
}

const {
  latitude = null,
  longitude = null,
  zoom = 14,
  height = 100,
  isStatic = true,
  withCircle = false,
  withMarker = false,
  circleOptions: circleOptionsProp = null,
  hasFullscreenControl = true,
  hasStreetViewControl = false,
  hasZoomControl = false,
  hasClickableIcons = false,
  hasTransitLayerControl = false,
  showMapTypeControl = false,
  showTransit = false,
} = defineProps<Props>();

const emit = defineEmits<{(e: 'ready', value: GoogleMapType): void}>();

const { googleApiKey: apiKey } = useAppDataStore();

const center = computed<GoogleMapLatLngLiteral | undefined>(() => {
  if (latitude === null || longitude === null) {
    return undefined;
  }

  return {
    lat: latitude,
    lng: longitude,
  };
});

const circleOptions = computed<CircleOptions>(() => ({
  center: center.value,
  radius: 300,
  strokeColor: getColorValue('blue'),
  strokeOpacity: 1,
  strokeWeight: 2,
  fillColor: getColorValue('blue'),
  fillOpacity: 0.2,
  clickable: false,
  ...circleOptionsProp,
}));

const style = {
  height: Number.isFinite(height)
    ? `${height}px`
    : height as string,
  width: 'auto',
};

const markerOptions = {
  position: center.value,
  icon: {
    url: '/images/ph_map-pin-espace.svg',
  },
};

const googleMap = ref<InstanceType<typeof GoogleMap> | null>(null);

const isTransitLayerVisible = ref<boolean>(showTransit);
const transitLayer = ref<GoogleMapTransitLayer | null>(null);

watch(
  isTransitLayerVisible,
  (visible) => {
    if (!googleMap.value?.map) {
      return;
    }

    if (!transitLayer.value) {
      transitLayer.value = new google.maps.TransitLayer();
    }

    transitLayer.value.setMap(visible ? googleMap.value.map : null);
  },
);

watch(
  () => googleMap.value?.map,
  (map) => {
    if (!map) {
      return;
    }

    if (!transitLayer.value) {
      transitLayer.value = new google.maps.TransitLayer();
    }

    if (isTransitLayerVisible.value) {
      transitLayer.value.setMap(map);
    }

    setTimeout(() => {
      google.maps.event.trigger(map, 'resize');

      if (!center.value) {
        return;
      }

      map.setCenter(center.value);
    }, 100);

    emit('ready', map);
  },
  { immediate: true },
);
</script>

<template>
  <GoogleMap
    ref="googleMap"
    class="relative"
    :api-key="apiKey"
    :center="center"
    :zoom="zoom"
    :style="style"
    :zoom-control="!isStatic && hasZoomControl"
    :map-type-control="!isStatic && showMapTypeControl"
    :scale-control="!isStatic"
    :street-view-control="!isStatic && hasStreetViewControl"
    :rotate-control="!isStatic"
    :fullscreen-control="!isStatic && hasFullscreenControl"
    :clickable-icons="!isStatic && hasClickableIcons"
    :disable-default-ui="true"
    :gesture-handling="!isStatic ? 'greedy' : 'none'"
  >
    <Circle
      v-if="withCircle"
      :options="circleOptions"
    />

    <Marker
      v-if="withMarker"
      :options="markerOptions"
    />

    <label
      v-if="hasTransitLayerControl"
      :class="[
        'absolute left-4 top-4',
        'rounded-sm border-none shadow-md',
        'bg-white',
        'p-2.5',
        'flex cursor-pointer items-center gap-2',
        'hover:bg-gray-lighter',
      ]"
    >
      <input
        v-model="isTransitLayerVisible"
        type="checkbox"
        class="size-4 cursor-pointer"
      >

      <Typography
        variant="body"
        class="select-none"
      >
        {{ trans('offer.modal.map.show_transports') }}
      </Typography>
    </label>

    <slot />
  </GoogleMap>
</template>
